import React, { useState, useEffect } from "react";
import {
    Input,
    DatePicker,
    Radio,
    Modal,
    Button,
    Space,
    Select,
    Spin,
} from "antd";
import {
    EditOutlined,
    CheckOutlined,
    CloseOutlined,
    LoadingOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import i18n from "../../../lang/i18n";
import { useTranslation } from "react-i18next";
import { getProvinces, getWardsByProvince, ProvinceItem, WardItem } from "../../../api/LocationInfo";

const { TextArea } = Input;
const { Option } = Select;

const normalize = (str: string) =>
    str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/đ/gi, "d").toLowerCase();

interface EditableRowProps {
    label: string;
    value?: string;
    fieldType?: "text" | "date" | "gender" | "textarea" | "select" | "address";
    options?: { label: string; value: any }[];
    provinceCode?: string;
    wardCode?: string;
    onSave?: (val: string) => Promise<void> | void;
    onSaveAddress?: (data: { province_code: string; ward_code: string; address: string }) => Promise<void> | void;
}

const EditableRow: React.FC<EditableRowProps> = ({
    label,
    value = "",
    fieldType = "text",
    options = [],
    provinceCode = "",
    wardCode = "",
    onSave,
    onSaveAddress,
}) => {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalSaving, setModalSaving] = useState(false);
    const [tempValue, setTempValue] = useState(value);
    const [selectedProvinceCode, setSelectedProvinceCode] = useState<string>(provinceCode);
    const [selectedWardCode, setSelectedWardCode] = useState<string>(wardCode);
    const [detail, setDetail] = useState(value);
    const [provinces, setProvinces] = useState<ProvinceItem[]>([]);
    const [wards, setWards] = useState<WardItem[]>([]);
    const [loadingWards, setLoadingWards] = useState(false);

    // Sync state with props when data loads
    useEffect(() => { setTempValue(value); }, [value]);
    useEffect(() => { setDetail(value); }, [value]);
    useEffect(() => { setSelectedProvinceCode(provinceCode); }, [provinceCode]);
    useEffect(() => { setSelectedWardCode(wardCode); }, [wardCode]);

    useEffect(() => {
        if (label === "Địa chỉ" || label === "Address") {
            getProvinces().then(setProvinces).catch(() => {});
        }
    }, [label]);

    useEffect(() => {
        if (selectedProvinceCode) {
            setLoadingWards(true);
            getWardsByProvince(selectedProvinceCode)
                .then(setWards)
                .catch(() => {})
                .finally(() => setLoadingWards(false));
        } else {
            setWards([]);
        }
    }, [selectedProvinceCode]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave?.(tempValue);
        } finally {
            setSaving(false);
            setEditing(false);
        }
    };
    const handleCancel = () => {
        setTempValue(value);
        setEditing(false);
    };

    const renderField = () => {
        switch (fieldType) {
            case "date":
                return (
                    <DatePicker
                        format={i18n.language === 'vi' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'}
                        value={tempValue ? dayjs(tempValue) : undefined}
                        onChange={(date) =>
                            setTempValue(date ? date.format("YYYY-MM-DD") : "")
                        }
                    />
                );

            case "gender":
                return (
                    <Radio.Group
                        value={tempValue !== "" ? Number(tempValue) : undefined}
                        onChange={(e) => setTempValue(String(e.target.value))}
                        className="dark:[&_.ant-radio-wrapper]:text-gray-200"
                    >
                        <Radio value={1}>{t('customer.male')}</Radio>
                        <Radio value={2}>{t('customer.female')}</Radio>
                        <Radio value={3}>{t('customer.other_gender')}</Radio>
                    </Radio.Group>
                );

            case "textarea":
                return (
                    <TextArea
                        className="dark:bg-[#1f1f1f] dark:border-[#303030] text-000000e0"
                        rows={3}
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                    />
                );

            case "select":
                return (
                    <Select
                        showSearch
                        filterOption={(input, option) =>
                            normalize(option?.label as string || "").includes(normalize(input))
                        }
                        className="dark:[&_.ant-select-selector]:bg-[#1f1f1f] 
                            dark:[&_.ant-select-selector]:border-[#303030]
                            dark:[&_.ant-select-selector]:text-gray-200"
                        value={tempValue ? Number(tempValue) : undefined}
                        onChange={(val) => setTempValue(String(val))}
                        style={{ width: 220 }}
                        options={options}
                    />
                );

            case "text":
            default:
                return (
                    <Input
                        className="dark:bg-[#1f1f1f] dark:border-[#303030] text-000000e0"
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                    />
                );
        }
    };

    const handleOk = async () => {
        setModalSaving(true);
        try {
            await onSaveAddress?.({
                province_code: selectedProvinceCode,
                ward_code: selectedWardCode,
                address: detail,
            });
        } finally {
            setModalSaving(false);
            setModalOpen(false);
        }
    };

    return (
        <>
            <div className="
                flex items-center justify-between py-3 border-b 
                border-gray-200 dark:border-gray-700
                text-gray-800 text-000000e0
            ">
                <div className="w-1/4 font-medium text-gray-600 dark:text-gray-400">
                    {label}
                </div>

                <div className="w-3/4 flex items-center justify-between">
                    {editing ? (
                        <>
                            <div className="flex-1">{renderField()}</div>

                            <Space className="ml-3">
                                {saving ? (
                                    <Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} spin />} />
                                ) : (
                                    <>
                                        <CheckOutlined
                                            className="text-[#3e91ab] cursor-pointer hover:opacity-80"
                                            onClick={handleSave}
                                        />
                                        <CloseOutlined
                                            className="text-red-500 cursor-pointer hover:opacity-80"
                                            onClick={handleCancel}
                                        />
                                    </>
                                )}
                            </Space>
                        </>
                    ) : (
                        <>
                            <div className="flex-1 text-gray-900 dark:text-gray-100">
                                {fieldType === "date" && value
                                    ? dayjs(value).format(i18n.language === 'vi' ? 'DD/MM/YYYY' : 'MM/DD/YYYY')
                                    : fieldType === "gender"
                                        ? ({ "1": t('customer.male'), "2": t('customer.female'), "3": t('customer.other_gender') }[String(value)] || "-")
                                        : fieldType === "select"
                                            ? (options.find(o => String(o.value) === String(value))?.label || "-")
                                            : (value || "-")}
                            </div>

                            <EditOutlined
                                className="text-blue-500 dark:text-blue-400 cursor-pointer hover:opacity-80"
                                onClick={() => {
                                    if (fieldType === "address") {
                                        setModalOpen(true);
                                    } else {
                                        setEditing(true);
                                    }
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* 🔥 Modal luôn nằm ngoài */}
            {(label === "Address" || label === "Địa chỉ") && (
                <Modal
                    className="dark:[&_.ant-modal-content]:bg-[#141414] 
                        dark:[&_.ant-modal-header]:bg-[#141414]
                        dark:[&_.ant-modal-title]:text-gray-200"
                    title={t('customer.edit_address')}
                    open={modalOpen}
                    onCancel={() => setModalOpen(false)}
                    footer={[
                        <Button key="cancel" onClick={() => setModalOpen(false)} disabled={modalSaving}>
                            {t('customer.cancel')}
                        </Button>,
                        <Button
                            key="ok"
                            type="primary"
                            loading={modalSaving}
                            onClick={handleOk}
                        >
                            {t('customer.save')}
                        </Button>,
                    ]}
                >
                    <Select
                        showSearch
                        filterOption={(input, option) =>
                            normalize(option?.label as string || "").includes(normalize(input))
                        }
                        placeholder={t('customer.select_province')}
                        value={selectedProvinceCode || undefined}
                        onChange={(val) => {
                            setSelectedProvinceCode(val);
                            setSelectedWardCode("");
                        }}
                        style={{ width: "100%", marginBottom: 12 }}
                        options={provinces.map(p => ({ label: p.full_name, value: p.code }))}
                    />

                    <Select
                        showSearch
                        filterOption={(input, option) =>
                            normalize(option?.label as string || "").includes(normalize(input))
                        }
                        placeholder={t('customer.select_ward')}
                        value={selectedWardCode || undefined}
                        onChange={setSelectedWardCode}
                        loading={loadingWards}
                        disabled={!selectedProvinceCode}
                        style={{ width: "100%", marginBottom: 12 }}
                        options={wards.map(w => ({ label: w.full_name || w.name, value: w.code || String(w.id) }))}
                    />

                    <Input.TextArea
                        placeholder={t('customer.detail_address')}
                        rows={3}
                        value={detail}
                        onChange={(e) => setDetail(e.target.value)}
                    />
                </Modal>
            )}
        </>
    );
};

export default EditableRow;
